import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import TaskList from "../../components/tasks/TaskList";
import projectService from "../../services/projectService";
// Import icons from react-icons
import { FaTasks, FaSearch, FaPlus, FaClipboardCheck, FaHourglassHalf, FaCheckCircle, FaLayerGroup, FaFilter, FaProjectDiagram } from "react-icons/fa";
// Import custom CSS
import "../../styles/taskManagement.css";

const tabs = [
  { key: "all", label: "All Tasks", icon: <FaLayerGroup className="mr-2" /> },
  { key: "pending", label: "Pending", icon: <FaHourglassHalf className="mr-2" /> },
  { key: "in_progress", label: "In Progress", icon: <FaTasks className="mr-2" /> },
  { key: "completed", label: "Completed", icon: <FaCheckCircle className="mr-2" /> },
  { key: "by_project", label: "By Project", icon: <FaProjectDiagram className="mr-2" /> },
];

const TasksPage = () => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);

  // Fetch projects for the By Project tab
  useEffect(() => {
    if (activeTab === "by_project") {
      const fetchProjects = async () => {
        setProjectsLoading(true);
        try {
          let projectsData;
          if (currentUser.role === "admin" || currentUser.role === "manager") {
            projectsData = await projectService.getAllProjects();
          } else {
            projectsData = await projectService.getMyProjects();
          }
          setProjects(projectsData);
        } catch (error) {
          console.error("Error fetching projects:", error);
        } finally {
          setProjectsLoading(false);
        }
      };
      fetchProjects();
    }
  }, [activeTab, currentUser]);

  return (
    <div className="task-container">
      {/* Header with modern gradient background */}
      <div className="task-header-gradient">
        <div className="task-header-pattern"></div>
        <div style={{position: 'relative', zIndex: 10, maxWidth: '1200px', margin: '0 auto'}}>
          <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: '1.5rem'}}>
            <div style={{display: 'flex', alignItems: 'center'}}>
              <div style={{background: 'rgba(255, 255, 255, 0.2)', padding: '0.75rem', borderRadius: '1rem', marginRight: '1rem'}}>
                <FaClipboardCheck style={{color: 'white', fontSize: '1.75rem'}} />
              </div>
            </div>
            {(currentUser?.role === "admin" || currentUser?.role === "manager" )&& (
              <Link
                to="/tasks/new"
                className="btn btn-primary"
                style={{display: 'flex', alignItems: 'center', background: 'white', color: 'var(--primary-dark)'}}
              >
                <FaPlus style={{marginRight: '0.5rem'}} /> Create Task
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Search bar and filters */}
      <div >
        <div className="search-container">
          <div className="search-input-wrapper">
            <div className="search-input-icon">
              <FaSearch />
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tasks by title, project or assignee..."
              className="search-input"
            />
          </div>
          {/* <div className="filter-label">
            <FaFilter className="filter-icon" />
            <span>Filter by:</span>
          </div> */}
        </div>

        {/* Tabs with icons and better styling */}
        <div className="tabs-container">
          <nav className="tabs-nav">

          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`tab-button ${activeTab === tab.key ? 'tab-button-active' : ''}`}
            >
              <div style={{display: 'flex', alignItems: 'center'}}>
                <span className="tab-icon">{tab.icon}</span>
                {tab.label}
              </div>
              {activeTab === tab.key && (
                <div className="tab-button-active-indicator" />
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Task List with enhanced styling */}
      <div
        key={activeTab}
        className="tab-content"
      >
        <div className="tab-content-header">
          <h2 className="tab-content-title">
            <span className="tab-content-title-icon">
              {tabs.find(tab => tab.key === activeTab)?.icon}
            </span>
            {activeTab === "all" ? "All Tasks" : 
             activeTab === "pending" ? "Pending Tasks" : 
             activeTab === "in_progress" ? "In Progress Tasks" : 
             activeTab === "by_project" ? "Projects" :
             "Completed Tasks"}
          </h2>
        </div>
        {activeTab === "by_project" ? (
          <div className="projects-grid">
            {projectsLoading ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <p className="loading-text">Loading projects...</p>
              </div>
            ) : projects.length === 0 ? (
              <div className="empty-container">
                <h3 className="empty-title">No Projects Found</h3>
                <p className="empty-description">You don't have any projects assigned yet.</p>
              </div>
            ) : (
              <div className="project-cards-container">
                {projects.map((project) => (
                  <div key={project.id || project._id || 'no-id'} className="project-card">
                    <div className="project-card-header">
                      <h3 className="project-card-title">{project.name || 'Unnamed Project'}</h3>
                      <div className="project-card-badge">{project.status || "Active"}</div>
                    </div>
                    <p className="project-card-description">{project.description || "No description available"}</p>
                    <div className="project-card-footer">
                      {project.id || project._id ? (
                        <Link to={`/tasks/project/${project.id || project._id}`} className="btn btn-primary btn-full">
                          View Tasks
                        </Link>
                      ) : (
                        <button disabled className="btn btn-primary btn-full" style={{ opacity: 0.7 }}>
                          Invalid Project ID
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <TaskList filter={activeTab !== "all" ? activeTab : null} search={search} />
        )}
      </div>
      </div>
    </div>
  );
};

export default TasksPage;
